import {
  Card,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  withStyles,
} from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { CancelIcon, CoreEditIcon, ValidateIcon } from 'components/Icons';
import { colors, fonts } from 'theme';

const useStyles = makeStyles({
  cardLabel: {
    fontWeight: 600,
    marginRight: '16px',
    width: '140px',
  },
  card: {
    padding: '39px',
    minHeight: '300px',
  },
  cardTitle: {
    fontSize: '24px',
    marginBottom: '15px',
    paddingBottom: '5px',
    color: colors.primary,
    borderBottom: `1px solid ${colors.greyLighten10}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: '48px',
  },
  actionButton: {
    width: '10px',
    height: '10px',
  },
  altTable: {
    marginTop: '50px',
  },
  altCardLabel: {
    fontWeight: 400,
    marginRight: '16px',
    width: '140px',
  },
  coreEditIcon: {
    color: '#2F3741',
    '&:hover': {
      color: '#0F626F',
      border: '0px solid transparent',
    },
  },
  altInputValue: {
    fontSize: '16px !important',
    '& input': {
      fontSize: '16px !important',
    },
  },
  divFlexGrow: {
    flexGrow: 1,
  },
  colorGrey: {
    color: colors.grey900,
  },
  colorPrimary: {
    color: colors.primary,
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '12',
    border: 'none',
    padding: '2px 0',
  },
})(TableCell);

const StyledTextField = withStyles({
  root: {
    width: '90%',
    '& input': {
      fontSize: '14px',
      padding: '3px 7px 0px 7px',
    },
  },
})(TextField);

export interface CardLine {
  key: string;
  value: string | number | null | undefined;
  label: string;
  editable?: boolean;
  validationRegex?: RegExp;
  maxLength?: number;
}

interface CardProps {
  title: string;
  lines: CardLine[];
  editable?: boolean;
  onSubmit?: (e: { [key: string]: string }) => void;
  resolver?: Resolver | undefined;
  preserveEditing: boolean;
}

export const NewStyledCard: React.FC<CardProps> = ({
  title,
  lines,
  editable,
  onSubmit,
  resolver,
  preserveEditing = false,
}) => {
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, setValue, handleSubmit, getValues, reset } = useForm({
    mode: 'onBlur',
    resolver,
  });

  const resetForm = useCallback(() => {
    reset(Object.fromEntries(lines.map(line => [line.key, line.value])));
  }, [lines, reset]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const [isEditing, setEditing] = useState<boolean>(false);

  const labelWidth = '29%';
  const separatorWidth = '10%';
  const valueWidth = '59%';

  const handleFormSubmit = () => {
    setIsSubmitting(true);
    if (!preserveEditing) {
      setEditing(false);
    }
    if (onSubmit) {
      onSubmit(getValues());
    }
    setIsSubmitting(false);
  };

  const handleReset = () => {
    resetForm();
    setEditing(false);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Card className={classes.card}>
        <Typography variant="h2" className={classes.cardTitle}>
          <div className={classes.divFlexGrow}>{title}</div>

          {editable && onSubmit && !isSubmitting && (
            <div>
              {isEditing && (
                <>
                  <IconButton className={classes.colorGrey} onClick={handleReset}>
                    <CancelIcon width={15} height={15} />
                  </IconButton>
                  <IconButton className={classes.colorPrimary} type="submit">
                    <ValidateIcon width={15} height={15} />
                  </IconButton>
                </>
              )}
              {!isEditing && (
                <IconButton className={classes.colorGrey} disabled={isEditing} onClick={() => setEditing(true)}>
                  <CoreEditIcon className={classes.coreEditIcon} />
                </IconButton>
              )}
            </div>
          )}
        </Typography>
        <Table className={classes.altTable}>
          <TableBody>
            {lines.map(line => (
              <TableRow key={line.key}>
                <StyledTableCell width={labelWidth}>
                  <span className={classes.altCardLabel}>{line.label}</span>
                </StyledTableCell>
                <StyledTableCell width={separatorWidth}>{': - '} </StyledTableCell>
                <StyledTableCell width={valueWidth}>
                  {isEditing && line.editable ? (
                    <Controller
                      name={line.key}
                      control={control}
                      render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
                        <StyledTextField
                          className={classes.altInputValue}
                          fullWidth
                          id={line.key}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputRef={ref}
                          onChange={e => {
                            if (line.validationRegex || line.maxLength) {
                              let newValue = e.target.value;
                              if (line.validationRegex) {
                                newValue = newValue.replace(line.validationRegex, '');
                              }
                              if (line.maxLength) {
                                newValue = newValue.slice(0, line.maxLength);
                              }
                              setValue(line.key, newValue);
                              return;
                            }
                            onChange(e);
                          }}
                          {...inputProps}
                        />
                      )}
                    />
                  ) : (
                    line.value || '-'
                  )}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </form>
  );
};
